/**
 * The small round buttons that sit in the corner of a side pane — close it,
 * and switch light or dark. ONE place for both benches (the chemistry lab and
 * the mechanics bench) so the same control looks and behaves the same
 * everywhere; restyle them in PaneButtons.css to change every one at once.
 *
 * Colours come from the host through three custom properties, so one rule
 * serves any palette:
 *   --pane-btn-ink         the icon
 *   --pane-btn-ink-strong  the icon on hover
 *   --pane-btn-hover       the fill on hover
 * Where they sit is the host's business — a class passed in positions them.
 */
import './PaneButtons.css';

export function CloseButton({ onClick, label = 'Close panel', className = '' }: {
    onClick: () => void;
    /** Accessible name and tooltip: say WHICH pane it closes where there are two. */
    label?: string;
    className?: string;
}) {
    return (
        <button type="button" className={`pane-icon-btn ${className}`.trim()} aria-label={label} title={label} onClick={onClick}>
            ✕
        </button>
    );
}

/**
 * The light/dark mark, for a host that draws its own button (a labelled pill, a
 * player's control strip) but must show the same sign as every other bench.
 * It shows where a press TAKES you — a sun on a dark surface, a moon on a light
 * one — as ThemeToggle does. `theme` is what is on screen now.
 */
export function ThemeIcon({ theme }: { theme: 'light' | 'dark' }) {
    return <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>;
}

export function ThemeToggle({ theme, onToggle, className = '' }: {
    theme: 'light' | 'dark';
    onToggle: () => void;
    className?: string;
}) {
    // The icon is where it would take you, as the tooltip says.
    const label = theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
    return (
        <button type="button" className={`pane-icon-btn pane-icon-btn--ring pane-theme-btn ${className}`.trim()} data-mode={theme} aria-label={label} title={label} onClick={onToggle}>
            <ThemeIcon theme={theme} />
        </button>
    );
}
